import * as React from 'react';

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  SwitchProps,
  Typography,
} from '@mui/material';

import { filter, isEqual } from 'lodash';

import { ShippingRegion } from '../../../graphql/API';
import useSubscriptionAccess from '../../../hooks/useSubscriptionAccess';
import { collabsDefaultFilters } from '../../../pages/Collabs/reducer';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import Button from '../../Button/Button';
import { ChipOptionsProps } from '../../ChipOptions/ChipOptions';
import { BlackIOSSwitch } from '../../IOSSwitch';
import ChipOptionContainer from './ChipOptionContainer';
import {
  SHIPPING_AVAILABILITY,
  followerOptions,
  makeDefaultValue,
  makeFiltersOutput,
  memberCategoryOptions,
  modelOptions,
  verifiedOptions,
} from './options';
import { PartialVenueListUsersFilter } from './types';

const shippingRegions = [
  ShippingRegion.us,
  ShippingRegion.asia,
  ShippingRegion.middleeast,
  ShippingRegion.eu,
  ShippingRegion.africa,
  ShippingRegion.latinamerica,
];

export type CollabsFiltersProps = {
  filters?: PartialVenueListUsersFilter;
  onChange?: (filters: PartialVenueListUsersFilter | undefined) => void;
  onClose?: () => void;
};

type SwitchOnChange = Exclude<SwitchProps['onChange'], undefined>;
type ChipOnChange = ChipOptionsProps['onChange'];

const CollabsFilters: React.FC<CollabsFiltersProps> = ({
  filters: defaultFilters,
  onChange,
  onClose,
}) => {
  const subscriptionAccess = useSubscriptionAccess();
  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const venueIsBrand =
    venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan;

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const [filters, setFilters] = React.useState<PartialVenueListUsersFilter | undefined>(
    defaultFilters,
  );

  const onApply = () => {
    if (onChange) onChange(filters);
    if (onClose) onClose();
  };

  const { fOptions, vOptions } = React.useMemo(
    () => ({
      fOptions: followerOptions(subscriptionAccess, venueIsBrand),
      vOptions: verifiedOptions(subscriptionAccess, venueIsBrand),
    }),
    [subscriptionAccess, venueIsBrand],
  );

  const handleOnChange = React.useCallback<ChipOnChange>(
    (e) => {
      const outputFilters = makeFiltersOutput(filters)[e.name](e.value);
      setFilters(outputFilters);
    },
    [filters],
  );

  const handleSwitchOnChange = React.useCallback<SwitchOnChange>(
    (_e, checked) => {
      const outputFilters = makeFiltersOutput(filters)['market'](checked);
      setFilters(outputFilters);
    },
    [filters],
  );

  const handleOnMarketChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, region: ShippingRegion) => {
      const checked = e.target.checked;
      const currentRegions = filters?.marketRegion || [];

      if (checked) {
        setFilters((prev) => ({
          ...prev,
          marketRegion: [...currentRegions, region],
        }));
      } else {
        setFilters((prev) => ({
          ...prev,
          marketRegion: currentRegions.filter((r) => r !== region),
        }));
      }
    },
    [filters?.marketRegion],
  );

  const onResetFilters = () => {
    setFilters({ ...collabsDefaultFilters, market: isOnlineBrand ? false : true });
  };

  const marginBottomButtons = filters?.marketRegion?.length === 0 ? 16 : 5;

  return (
    <Box>
      {!venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) ? (
        <>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Typography fontWeight='bold' fontSize={'15px'}>
              Show me members in all market
            </Typography>
            <Switch
              onChange={handleSwitchOnChange}
              checked={!filters?.market}
              value=''
              inputProps={{ 'aria-label': 'market switch' }}
              disabled={venueIsBrand}
            />
          </Box>

          <Divider sx={{ my: 4 }} />
        </>
      ) : null}

      <Stack spacing={4}>
        {isOnlineBrand ? (
          <Box display={'flex'} flexDirection={'column'} gap={'10px'}>
            <Typography component='div' variant='caption' fontWeight='500' fontSize={'15px'}>
              Market
            </Typography>

            <Box>
              <FormControlLabel
                sx={{
                  m: 0,
                  gap: '6px',
                  fontSize: '4px',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '15px',
                  },
                }}
                control={
                  <BlackIOSSwitch
                    checked={filters?.marketRegion?.length === 0 || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) {
                        setFilters((prev) => ({
                          ...prev,
                          market: false,
                          marketRegion: [],
                        }));
                      } else {
                        setFilters((prev) => ({
                          ...prev,
                          market: false,
                          marketRegion: [ShippingRegion.us],
                        }));
                      }
                    }}
                  />
                }
                label={SHIPPING_AVAILABILITY['global']}
                labelPlacement='start'
              />
            </Box>
            {(filters?.marketRegion?.length || 0) > 0 && (
              <Box>
                <Grid container>
                  {shippingRegions.map((value) => (
                    <Grid item xs={4}>
                      <FormControlLabel
                        key={value}
                        control={
                          <Checkbox
                            disabled={filters?.marketRegion?.includes(ShippingRegion.global)}
                            checked={filters?.marketRegion?.includes(value)} // Check if selected
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleOnMarketChange(e, value)
                            }
                            sx={{ color: '#DADADA' }}
                          />
                        }
                        label={SHIPPING_AVAILABILITY[value]}
                        sx={{
                          '& .MuiSvgIcon-root': { fontSize: 24 },
                          '& .MuiFormControlLabel-label': {
                            fontSize: '15px',
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        ) : null}

        <ChipOptionContainer
          defaultValue={makeDefaultValue['follower'](filters)}
          name='follower'
          label='Followers'
          options={fOptions}
          onChange={handleOnChange}
        />

        {!venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) && (
          <ChipOptionContainer
            defaultValue={makeDefaultValue['lifestyle'](filters)}
            name='lifestyle'
            label='Member category'
            options={memberCategoryOptions(venueIsBrand)}
            onChange={handleOnChange}
            eyeIconForMemberCategory
          />
        )}

        <ChipOptionContainer
          defaultValue={makeDefaultValue['model'](filters)}
          name='model'
          label='Models'
          options={modelOptions(venueIsBrand)}
          onChange={handleOnChange}
        />

        <ChipOptionContainer
          verifiedIcon
          defaultValue={makeDefaultValue['instagramVerified'](filters)}
          name='instagramVerified'
          label='Verified on Instagram'
          options={vOptions}
          onChange={handleOnChange}
        />
      </Stack>

      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        mt={venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) ? marginBottomButtons : 3}
      >
        <Button
          sx={{ textDecoration: 'underline' }}
          onClick={onResetFilters}
          disabled={venueIsBrand}
        >
          Reset Filters
        </Button>
        <Box display={'flex'} alignItems={'center'} gap={'1rem'}>
          <Button
            variant='outlined'
            sx={{ padding: '6px 20px' }}
            onClick={onClose}
            disabled={venueIsBrand}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            sx={{ padding: '6px 20px' }}
            onClick={onApply}
            disabled={isEqual(defaultFilters, filters) || venueIsBrand}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CollabsFilters;
